import React, { useEffect } from 'react';
import { ToastCard, ToastTone } from './ToastCard';

/**
 * The operational view's own toast. It used to be a second notification design — translucent
 * tinted panel, its own icon set, its own type scale — sitting on the same screen as the
 * app-wide one. It renders the shared ToastCard now; what stays here is the only thing that
 * was ever specific to it: a self-closing timer and the stack's position.
 */

export type ToastType = 'success' | 'error' | 'warning' | 'info';

export interface ToastProps {
  id: string;
  type: ToastType;
  title: string;
  message?: string;
  duration?: number;
  onClose: (id: string) => void;
}

/** This panel's vocabulary, in the card's. They differ only in that it has no loading state. */
const TONE: Record<ToastType, ToastTone> = {
  success: 'success',
  error: 'error',
  warning: 'warning',
  info: 'info',
};

export const Toast: React.FC<ToastProps> = ({
  id,
  type,
  title,
  message,
  duration = 5000,
  onClose
}) => {
  useEffect(() => {
    if (duration > 0) {
      const timer = setTimeout(() => {
        onClose(id);
      }, duration);

      return () => clearTimeout(timer);
    }
  }, [id, duration, onClose]);

  return (
    <div className="animate-in slide-in-from-right-full duration-300">
      <ToastCard
        tone={TONE[type]}
        title={title}
        body={message}
        onDismiss={() => onClose(id)}
      />
    </div>
  );
};

export interface ToastContainerProps {
  toasts: ToastProps[];
  onClose: (id: string) => void;
}

export const ToastContainer: React.FC<ToastContainerProps> = ({ toasts, onClose }) => {
  return (
    <div className="fixed top-[70px] right-4 z-[99] flex flex-col gap-2 max-w-sm w-full pointer-events-none">
      <div className="pointer-events-auto flex flex-col gap-2">
        {toasts.map((toast) => (
          <Toast key={toast.id} {...toast} onClose={onClose} />
        ))}
      </div>
    </div>
  );
};
